import type { ErrorCode } from '../../errors';
import type { TaskDto } from '../../platform/tasks/dto';
import { MEDIA_FORMATS, MediaKind } from './formats';

// MED-UC-8, MED-UC-9, ADR-020: хранилище блога и пережатие исходников.

export enum RecompressImageTarget {
  Jpeg = 'jpeg',
  Webp = 'webp',
  Jxl = 'jxl',
}

export enum RecompressVideoTarget {
  /** MP4, H.264 и AAC. */
  H264 = 'h264',
}

export enum RecompressPreset {
  Economy = 'economy',
  Balanced = 'balanced',
  Quality = 'quality',
  Custom = 'custom',
}

export interface RecompressImageParamsDto {
  target: RecompressImageTarget;
  /** Общая шкала 1–100: сервер переводит её в параметры кодека. */
  quality: number;
  /** Наибольшая длинная сторона, px; null — без уменьшения. */
  maxLongSide: number | null;
}

export interface RecompressVideoParamsDto {
  target: RecompressVideoTarget;
  /** CRF x264: меньше — лучше и тяжелее. */
  crf: number;
  /** Наибольшая короткая сторона кадра, px: вертикальный ролик не сжимается вдвое; null — без уменьшения. */
  maxShortSide: number | null;
  /** Ограничение частоты кадров; null — как у исходника. */
  maxFps: number | null;
  audioKbps: number;
}

// MED-UC-9
export interface RecompressParamsDto {
  /** Форматы исходников — расширения из MEDIA_FORMATS. */
  sources: string[];
  /** Обязательны, если среди исходников есть фото. */
  image: RecompressImageParamsDto | null;
  /** Обязательны, если среди исходников есть видео. */
  video: RecompressVideoParamsDto | null;
  /** MED-UC-9.CC-2: наименьшая выгода, %. */
  minSavingsPercent: number;
  /** Из какого пресета развёрнуты параметры — только для отчёта. */
  preset: RecompressPreset;
}

interface Range {
  min: number;
  max: number;
}

/** Границы параметров — проверяют и схема API, и форма. */
export const RECOMPRESS_LIMITS: Readonly<Record<'quality' | 'maxLongSide' | 'crf' | 'maxShortSide' | 'maxFps' | 'audioKbps' | 'minSavingsPercent', Range>> = {
  quality: { min: 1, max: 100 },
  maxLongSide: { min: 320, max: 16_384 },
  crf: { min: 16, max: 35 },
  maxShortSide: { min: 240, max: 4320 },
  maxFps: { min: 10, max: 120 },
  audioKbps: { min: 32, max: 320 },
  minSavingsPercent: { min: 0, max: 90 },
};

export interface RecompressPresetValues {
  image: Omit<RecompressImageParamsDto, 'target'>;
  video: Omit<RecompressVideoParamsDto, 'target'>;
  minSavingsPercent: number;
}

/**
 * Пресеты (ADR-020). «Экономно» — под публикацию: Telegram сам ужимает фото до 2560 px по длинной
 * стороне. «Качество» кадр не уменьшает.
 */
export const RECOMPRESS_PRESETS: Readonly<Record<Exclude<RecompressPreset, RecompressPreset.Custom>, RecompressPresetValues>> = {
  [RecompressPreset.Economy]: {
    image: { quality: 72, maxLongSide: 2560 },
    video: { crf: 28, maxShortSide: 720, maxFps: 30, audioKbps: 96 },
    minSavingsPercent: 10,
  },
  [RecompressPreset.Balanced]: {
    image: { quality: 82, maxLongSide: 4096 },
    video: { crf: 23, maxShortSide: 1080, maxFps: 60, audioKbps: 128 },
    minSavingsPercent: 10,
  },
  [RecompressPreset.Quality]: {
    image: { quality: 92, maxLongSide: null },
    video: { crf: 19, maxShortSide: null, maxFps: null, audioKbps: 192 },
    minSavingsPercent: 10,
  },
};

/** GIF не пережимается: кадр JPEG, WebP или JPEG XL убил бы анимацию. */
export const RECOMPRESSIBLE_FORMATS = MEDIA_FORMATS.filter((format) => format.ext !== 'gif');

export function recompressSourceKind(ext: string): MediaKind | null {
  return RECOMPRESSIBLE_FORMATS.find((format) => format.ext === ext)?.kind ?? null;
}

export interface RecompressFailureDto {
  mediaId: string;
  name: string;
  code: ErrorCode;
}

// MED-UC-9, TASK-UC-3: отчёт задачи пережатия.
export interface RecompressReportDto {
  /** Пережато. */
  converted: number;
  /** MED-UC-9.CC-2: стало бы не меньше на заданную долю — оставлено как было. */
  notWorth: number;
  /** MED-UC-9.CC-4: не удалось пережать. */
  failed: number;
  /** MED-UC-9.CC-5: удалено, изменено или стало дублем во время задачи. */
  skipped: number;
  /** Объём пережатых медиа (все их файлы) до и после. */
  bytesBefore: number;
  bytesAfter: number;
  /** Первые ошибки по шагам (TASK_REPORT_MAX_FAILURES); у администратора вне блога — пусто. */
  failures: RecompressFailureDto[];
  /** DEMO-RULE-5: имитация, файлы не менялись. */
  demo: boolean;
}

export const EMPTY_RECOMPRESS_REPORT: RecompressReportDto = {
  converted: 0,
  notWorth: 0,
  failed: 0,
  skipped: 0,
  bytesBefore: 0,
  bytesAfter: 0,
  failures: [],
  demo: false,
};

export type RecompressTaskDto = TaskDto<RecompressParamsDto, RecompressReportDto>;

// MED-UC-10
export interface StorageRecountReportDto {
  /** Медиа, у которых дописаны размеры. */
  measured: number;
  /** MED-UC-10.CC-1: файла нет в хранилище. */
  missing: number;
}

// MED-UC-8
export interface MediaFormatUsageDto {
  ext: string;
  kind: MediaKind;
  count: number;
  /** Все файлы медиа этого формата: оригиналы, JPEG-копии и превью (MED-RULE-5). */
  bytes: number;
}

/** MED-UC-9.CC-6: что умеет обработчик медиа сервера. */
export interface RecompressCapabilitiesDto {
  available: boolean;
  imageTargets: RecompressImageTarget[];
  videoTargets: RecompressVideoTarget[];
  /** MED-UC-9.CC-4: HDR-видео приводится к обычному диапазону. */
  hdrToSdr: boolean;
}

/** DEMO-RULE-5: имитации ffmpeg не нужен — в демо доступны все форматы. */
export const DEMO_RECOMPRESS_CAPABILITIES: RecompressCapabilitiesDto = {
  available: true,
  imageTargets: Object.values(RecompressImageTarget),
  videoTargets: Object.values(RecompressVideoTarget),
  hdrToSdr: true,
};

export interface MediaStorageDto {
  count: number;
  totalBytes: number;
  /** MED-UC-10: у стольких медиа размер превью или копии ещё не замерен. */
  unmeasured: number;
  formats: MediaFormatUsageDto[];
  recompress: RecompressCapabilitiesDto;
}

/** MED-UC-3, MED-UC-9.CC-3: из чего и когда пережато медиа. */
export interface MediaRecompressedDto {
  fromExt: string;
  fromSize: number;
  at: string;
}
